import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { useNavigate } from '../hooks/useNavigate';
import { FOLDER_404_ERROR, ProviderType, SearchObjectsResponseDTO } from '../DocumentVault.meta';
import { useFilterableYearOption } from '../state/selectors';
import { queryKeys } from './queryKeys';
import { useFidelityTokenId } from '../hooks/useFidelityTokenId';
import { useDocumentVaultContext } from '../context/useDocumentVaultContext';

interface SearchEntitiesParams {
  clientId: string | number;
  path: string | null;
  providerType: ProviderType;
  year?: number;
  tokenId?: string;
  searchTerm?: string;
}

interface CancelablePromise extends Promise<SearchObjectsResponseDTO> {
  cancel(): void;
}

const searchEntities = ({
  clientId,
  path,
  providerType,
  year,
  tokenId,
  searchTerm,
}: SearchEntitiesParams) => {
  const controller = new AbortController();
  const { signal } = controller;

  const promise = authFetch(
    urlWithParams(
      '/Consumer/DocumentVault/SearchObjects',
      {
        clientId,
        path: path || null,
        providerType,
        year,
        tokenId,
        query: searchTerm,
      },
      { skipNulls: true },
    ),
    {
      signal,
    },
  ) as CancelablePromise;

  promise.cancel = () => controller.abort();

  return promise;
};

interface Params {
  path: string;
  clientId: string;
  providerType: ProviderType;
  filterableByYear: boolean;
  searchTerm: string;
}

export const useSearchEntitiesQuery = ({
  clientId,
  path,
  providerType,
  filterableByYear,
  searchTerm,
}: Params) => {
  const { value: selectedYear } = useFilterableYearOption();

  const { getCurrentTokenId } = useFidelityTokenId();

  const tokenId = getCurrentTokenId();

  const navigate = useNavigate();

  const { forceYear } = useDocumentVaultContext();

  const year = forceYear || selectedYear;

  return useQuery(
    queryKeys.searchDocumentVaultEntities(
      clientId,
      path,
      providerType,
      filterableByYear ? year : null,
      providerType === ProviderType.All ? undefined : tokenId,
      searchTerm,
    ),

    () =>
      searchEntities({
        clientId,
        path: decodeURIComponent(path),
        providerType,
        year: filterableByYear ? year : undefined,
        tokenId: providerType === ProviderType.All ? undefined : tokenId,
        searchTerm,
      }),

    {
      onError(err) {
        // Navigate to home if folder doesn't exist.
        if (err.message === FOLDER_404_ERROR) {
          navigate({ path: '', providerType: ProviderType.All });
        }
      },

      retry(failureCount, err) {
        // Do not retry for a folder that does not exist
        return !(err.message === FOLDER_404_ERROR || failureCount > 2);
      },

      enabled: path !== undefined && !!searchTerm,
    },
  );
};
